import { type ComponentProps, type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';

import { Media } from '../StrokeIcons';

// Format seconds to mm:ss
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export type MusicPlayerProps = ComponentProps<'div'> & {
  src?: string;
};

export function MusicPlayer({ src, className }: MusicPlayerProps) {
  const ref = useRef<HTMLAudioElement>(null);
  const [percentage, setPercentage] = useState(0);
  const [isPaused, setIsPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = ref.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration) {
        setPercentage((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      audio.currentTime = 0;
      setIsPaused(true);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handlePlay = () => setIsPaused(false);
    const handlePause = () => setIsPaused(true);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  const onToggle = async (e?: MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (ref.current?.paused) {
      await ref.current?.play();
    } else {
      ref.current?.pause();
    }
  };

  const onSeek = async (seekPercentage: number) => {
    if (isPaused) {
      await onToggle();
    }
    if (ref.current?.duration) {
      ref.current.currentTime = (seekPercentage * ref.current.duration) / 100;
    }
  };

  return (
    <div className={className}>
      <audio src={src} ref={ref} />
      <div className="flex w-full flex-row items-center">
        <Bar
          percentage={percentage}
          currentTime={currentTime}
          duration={duration}
          onSeek={onSeek}
          playPauseContent={
            <button
              onClick={onToggle}
              aria-label={isPaused ? 'Play' : 'Pause'}
              // z-10 keeps the button above the (relative, later-in-DOM) time-label row so its
              // transparent left padding can't steal the click and turn a pause into a seek.
              className="absolute left-4 top-1/2 z-10 flex h-6 w-auto -translate-y-1/2 cursor-pointer text-white/75 transition-colors hover:text-white"
            >
              {isPaused && <Media.Play className="h-full w-auto" />}
              {!isPaused && <Media.PauseCircle className="h-full w-auto" />}
            </button>
          }
        />
      </div>
    </div>
  );
}

type BarProps = {
  percentage: number;
  currentTime?: number;
  duration?: number;
  onSeek?: (percentage: number) => void;
  playPauseContent?: ReactNode;
};

function Bar({ percentage, currentTime, duration, onSeek, playPauseContent }: BarProps) {
  const onBarClicked = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const elWidth = e.currentTarget.clientWidth;
    const clickedAtWidth = e.clientX - e.currentTarget.getBoundingClientRect().left;
    const clickPercentage = (clickedAtWidth / elWidth) * 100;
    onSeek?.(clickPercentage);
  };

  return (
    <div
      className="relative flex h-12 w-full min-w-[120px] cursor-pointer items-center overflow-hidden rounded-lg bg-slate-600"
      onClick={onBarClicked}
    >
      <div
        className="absolute inset-y-0 left-0 bg-[#0B2058] transition-[width] duration-100"
        style={{ width: `${percentage}%` }}
      />
      {playPauseContent}
      {/* Time labels are non-interactive; pointer-events-none so this overlay never intercepts
          clicks meant for the play/pause button (left) or the seek bar (elsewhere). */}
      <div className="pointer-events-none relative flex w-full items-center gap-2 pl-12 pr-3 text-sm font-medium text-white">
        {currentTime != null && <span>{formatTime(currentTime)}</span>}
        {duration != null && !isNaN(duration) && <span className="ml-auto">{formatTime(duration)}</span>}
      </div>
    </div>
  );
}
